import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X,
  ChevronDown,
  ChevronRight,
  Check,
  Play,
  Rocket,
  LayoutDashboard,
  FolderKanban,
  FileSearch,
  Settings,
  Palette,
  HelpCircle,
  Search,
} from 'lucide-react';
import { useOnboardingStore, TourId } from '../../store/onboardingStore';

interface TourItem {
  id: TourId;
  label: string;
  description?: string;
}

interface TOCSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  tours: TourItem[];
}

const tocSections: TOCSection[] = [
  {
    id: 'quickstart',
    label: 'Quick Start',
    icon: <Rocket className="w-4 h-4" />,
    tours: [
      { id: 'welcome', label: 'Full Platform Walkthrough', description: 'Complete tour of all features' },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-4 h-4" />,
    tours: [
      { id: 'dashboard', label: 'Dashboard Overview', description: 'Pipeline, stats, and navigation' },
    ],
  },
  {
    id: 'pipeline',
    label: 'Pipeline Sections',
    icon: <FolderKanban className="w-4 h-4" />,
    tours: [
      { id: 'pipeline-new', label: 'New in Salesforce', description: 'Cases available for download' },
      { id: 'pipeline-queue', label: 'Queue', description: 'Active translation work' },
      { id: 'pipeline-approved', label: 'Approved Cases', description: 'Ready for upload' },
      { id: 'pipeline-uploaded', label: 'Uploaded Cases', description: 'Successfully completed' },
      { id: 'pipeline-returned', label: 'Returned Cases', description: 'Cases sent back for revision' },
      { id: 'pipeline-issues', label: 'Issues', description: 'Lost and found cases' },
    ],
  },
  {
    id: 'review',
    label: 'Case Review',
    icon: <FileSearch className="w-4 h-4" />,
    tours: [
      { id: 'case-review', label: 'Review Page Walkthrough', description: 'Open a case first, then start this tour' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <Settings className="w-4 h-4" />,
    tours: [
      { id: 'settings', label: 'Settings Overview', description: 'LLM providers and prompts' },
    ],
  },
  {
    id: 'interface',
    label: 'Interface',
    icon: <Palette className="w-4 h-4" />,
    tours: [
      { id: 'sidebar', label: 'Sidebar & Navigation', description: 'Theme, sync, and navigation' },
    ],
  },
];

// Map tour IDs to their required route and section (if applicable)
const tourRouteMap: Record<TourId, { route: string; section?: string }> = {
  'welcome': { route: '/' },
  'dashboard': { route: '/' },
  'pipeline-new': { route: '/', section: 'new' },
  'pipeline-queue': { route: '/', section: 'queue' },
  'pipeline-approved': { route: '/', section: 'approved' },
  'pipeline-uploaded': { route: '/', section: 'uploaded' },
  'pipeline-returned': { route: '/', section: 'returned' },
  'pipeline-issues': { route: '/', section: 'issues' },
  'case-review': { route: '/review' }, // Special case - will be handled differently
  'settings': { route: '/settings' },
  'sidebar': { route: '/' },
};

export function OnboardingTOC() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isTOCOpen, closeTOC, startTour, completedTours, resetProgress } = useOnboardingStore();
  const [expandedSections, setExpandedSections] = useState<string[]>(['quickstart']);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isTOCOpen) return null;

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleStartTour = (tourId: TourId) => {
    const routeInfo = tourRouteMap[tourId];

    // Case review tour is special - only available when already on a review page
    if (tourId === 'case-review') {
      if (!location.pathname.startsWith('/review/')) {
        // Can't start case review tour without being on a review page
        // Just close the TOC and don't start tour
        closeTOC();
        return;
      }
      startTour(tourId);
      return;
    }

    // Check if we need to navigate
    const currentRoute = location.pathname;
    const needsNavigation =
      (routeInfo.route === '/settings' && currentRoute !== '/settings') ||
      (routeInfo.route === '/' && currentRoute !== '/');

    if (needsNavigation) {
      // Navigate with section param if needed
      const targetUrl = routeInfo.section
        ? `${routeInfo.route}?section=${routeInfo.section}`
        : routeInfo.route;
      navigate(targetUrl);

      // Delay tour start to allow navigation and DOM to settle
      setTimeout(() => {
        startTour(tourId);
      }, 300);
    } else {
      // Already on correct page, start tour immediately
      startTour(tourId);
    }
  };

  const filteredSections = searchQuery
    ? tocSections.map((section) => ({
        ...section,
        tours: section.tours.filter(
          (tour) =>
            tour.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            tour.description?.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((section) => section.tours.length > 0)
    : tocSections;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={closeTOC}
      />

      {/* Slide-out Panel */}
      <div className="fixed right-0 top-0 h-full w-[380px] bg-white dark:bg-dark-elevated shadow-2xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Help & Tutorials
            </h2>
          </div>
          <button
            onClick={closeTOC}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-gray-100 dark:border-dark-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tutorials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-dark-border rounded-lg bg-gray-50 dark:bg-dark-recessed focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400 text-gray-800 dark:text-gray-200"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 py-4">
          {filteredSections.map((section) => (
            <div key={section.id} className="mb-2">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors"
              >
                <span className="text-gray-500 dark:text-gray-400">
                  {expandedSections.includes(section.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </span>
                <span className="text-purple-600 dark:text-purple-400">
                  {section.icon}
                </span>
                <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">
                  {section.label}
                </span>
                {/* Completion indicator */}
                {section.tours.every((t) => completedTours.includes(t.id)) && (
                  <Check className="w-4 h-4 text-green-500 ml-auto" />
                )}
              </button>

              {/* Section Tours */}
              {expandedSections.includes(section.id) && (
                <div className="ml-6 mt-1 space-y-1">
                  {section.tours.map((tour) => {
                    const isCompleted = completedTours.includes(tour.id);
                    return (
                      <div
                        key={tour.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-hover group"
                      >
                        {/* Completion check */}
                        <span className={`w-4 h-4 flex-shrink-0 ${isCompleted ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}>
                          {isCompleted ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <div className="w-3 h-3 rounded-full border-2 border-current ml-0.5" />
                          )}
                        </span>

                        {/* Tour info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-700 dark:text-gray-200">
                            {tour.label}
                          </div>
                          {tour.description && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {tour.description}
                            </div>
                          )}
                        </div>

                        {/* Start button */}
                        <button
                          onClick={() => handleStartTour(tour.id)}
                          className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded transition-all"
                        >
                          <Play className="w-3 h-3" />
                          Start
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>
              {completedTours.length} of {tocSections.reduce((acc, s) => acc + s.tours.length, 0)} completed
            </span>
            <button
              onClick={resetProgress}
              className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              Reset progress
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
